import { hotkeysCoreFeature, syncDataLoaderFeature } from '@headless-tree/core';
import { useTree } from '@headless-tree/react';
import type React from 'react';
import { useMemo } from 'react';
import { Tree, TreeItem, TreeItemLabel } from '@/components/reui/tree';
import { Badge } from '@/components/ui/badge';
import {
  buildOrganisationPreviewTree,
  type OrgPreviewItem,
} from '@/lib/organisationStructurePreview';
import type { OrganisationStructure } from '@/services/organisationStructureService';

const TREE_INDENT = 20;

interface OrganisationStructurePreviewProps {
  rows: OrganisationStructure[];
  onSelectRow?: (row: OrganisationStructure) => void;
}

function PreviewDeptTeamLabel({ item }: { item: OrgPreviewItem }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1">
      <span>{item.name}</span>
      {item.leadName ? (
        <span className="font-normal text-[10px] text-text-secondary">lead by {item.leadName}</span>
      ) : null}
    </span>
  );
}

function PreviewEmployeeLabel({ item }: { item: OrgPreviewItem }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>👤 {item.name}</span>
      {item.derivedRole ? <Badge variant="secondary">{item.derivedRole}</Badge> : null}
      {item.employeeType === 'admin' ? <Badge variant="outline">Admin</Badge> : null}
      {item.specialRole === 'MAN' ? <Badge variant="outline">{item.specialRole}</Badge> : null}
    </span>
  );
}

export const OrganisationStructurePreview: React.FC<OrganisationStructurePreviewProps> = ({
  rows,
  onSelectRow,
}) => {
  const preview = useMemo(() => buildOrganisationPreviewTree(rows), [rows]);
  const rowById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);

  const allExpanded = useMemo(
    () =>
      Object.values(preview.items)
        .filter((item) => item.children.length > 0)
        .map((item) => item.id),
    [preview]
  );

  const tree = useTree<OrgPreviewItem>({
    state: { expandedItems: allExpanded },
    setState: () => {},
    indent: TREE_INDENT,
    rootItemId: preview.rootItemId,
    getItemName: (item) => {
      const data = item.getItemData();
      return data.leadName ? `${data.name} lead by ${data.leadName}` : data.name;
    },
    isItemFolder: (item) => (item.getItemData()?.children?.length ?? 0) > 0,
    dataLoader: {
      getItem: (itemId) => preview.items[itemId],
      getChildren: (itemId) => preview.items[itemId]?.children ?? [],
    },
    features: [syncDataLoaderFeature, hotkeysCoreFeature],
  });

  if (preview.activeRowCount === 0) {
    return (
      <div className="p-8 text-center text-text-secondary">
        <p>No active organisation structure rows to preview.</p>
        {preview.inactiveRowCount > 0 ? (
          <p className="mt-2 text-sm">
            {preview.inactiveRowCount} inactive row
            {preview.inactiveRowCount === 1 ? '' : 's'} hidden (past end date or future start).
          </p>
        ) : null}
      </div>
    );
  }

  const departmentCount = Object.values(preview.items).filter(
    (item) => item.nodeType === 'department'
  ).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {departmentCount === 0 ? (
        <p className="text-sm text-text-secondary">
          No departments found in active rows. Check department numbers on your data.
        </p>
      ) : null}

      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <Tree key={allExpanded.join('|')} className="w-full" indent={TREE_INDENT} tree={tree}>
          {tree.getItems().map((item) => {
            if (item.getId() === preview.rootItemId) return null;

            const data = item.getItemData();
            const isEmployee = data.nodeType === 'employee';
            const isDeptOrTeam = data.nodeType === 'department' || data.nodeType === 'team';
            const sourceRow = data.sourceRowId ? rowById.get(data.sourceRowId) : undefined;

            return (
              <TreeItem
                key={item.getId()}
                item={item}
                render={
                  isEmployee && onSelectRow && sourceRow ? (
                    <button
                      type="button"
                      className="block w-full border-0 bg-transparent p-0 text-left"
                      onClick={() => onSelectRow(sourceRow)}
                    />
                  ) : undefined
                }
              >
                {isEmployee ? (
                  <TreeItemLabel>
                    <PreviewEmployeeLabel item={data} />
                  </TreeItemLabel>
                ) : isDeptOrTeam ? (
                  <TreeItemLabel className="text-xs">
                    <PreviewDeptTeamLabel item={data} />
                  </TreeItemLabel>
                ) : (
                  <TreeItemLabel />
                )}
              </TreeItem>
            );
          })}
        </Tree>
      </div>
    </div>
  );
};
